/**
 * `DocumentBody`, made to write. Same surface, same classes, same Tiptap — the one difference is
 * that this instance accepts input and owes every keystroke a save.
 *
 * The rendering is deliberately identical to the read-only twin: the same `useDocumentEditor` over
 * the same markdown converters, wrapped in the same `docProse` + `DocumentBody.module.css` pair, so
 * swapping the two on the stage moves the caret in and nothing else. Editing adds only what the
 * canvas's own editor (`BirtvyDocEditor`) adds: the full rich schema is already in the shared hook,
 * so the additions are the selection `FormattingToolbar` and — where the read view draws a separate
 * `<h1>` — a title FIELD in its place, wearing the identical `docCardTitle` class.
 *
 * TWO TITLES, ONE RULE (the canvas's). A doc whose name is derivable from its body gets no field:
 * its title IS the first line, edited in the body, re-derived on save — `updateDoc`'s rule. A doc
 * with a name of its own (a Library document whose title is a column on the row, not a line of its
 * text — `DocumentBody`'s `showTitle` test, reused verbatim) gets the field, and the typed name wins
 * over derivation — `placeDoc`'s rule. Which mode a session is in is decided ONCE per card: a field
 * that popped in and out as the first line changed mid-edit would eat the caret.
 *
 * WHERE THE SAVE COMES FROM. Not props — the stage that mounts this is a pure function of the
 * entity and holds no `mutate`. The workspace registers its save on `docEditBus`; this editor
 * debounces into it (800ms, flushed on unmount and on card switch) and reports the buffer through
 * `onDirty`. Dirty means "keystrokes buffered here", not "not yet on the server" — the bus owner
 * owns the async truth past the handoff. With no bus registered the editor renders read-only and
 * warns, rather than accepting edits it could never save.
 *
 * HOST CONTRACT: register a `docEditBus` save (only while the blob is writable), and mount this
 * keyed by the card — `<DocumentEditor key={doc.id} …/>` — so a selection switch remounts rather
 * than re-seeds; the internal `doc.id` effect still flushes the outgoing card's buffer either way.
 */
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { EditorContent } from '@tiptap/react';
import guard from './contentGuard.module.css';
import type { BirtvyDoc } from '~/design-system/components/DeckGen/birtvy/birtvyDoc';
import {
  DOC_TITLE_PLACEHOLDER,
  deriveDocTitle,
  useDocumentEditor,
} from '~/design-system/components/DeckGen/components/editor/useDocumentEditor';
import proseStyles from '~/design-system/components/DeckGen/components/editor/UserDoc.module.css';
import { FormattingToolbar } from '~/design-system/components/OutlinePanel/FormattingToolbar';
import { FORMATTING_BUTTONS } from '~/design-system/components/OutlinePanel/util/formatting-toolbar.config';
import { cn } from '~/lib/utils';
import bodyStyles from './DocumentBody.module.css';
import {
  readDocEditBus,
  serverDocEditBus,
  subscribeDocEditBus,
  type DocEditPatch,
} from './docEditBus';
import styles from './DocumentEditor.module.css';

/** Long enough to bridge a typing burst, short enough that "it saves as I type" stays true. The
 *  canvas saves per keystroke locally and flushes the Library on close; here every flush is a full
 *  `saveDocumentEverywhere`, so the debounce is what keeps a sentence from being five blob writes. */
const SAVE_DEBOUNCE_MS = 800;

/** `DocumentBody`'s `showTitle` test, verbatim — the same doc must grow the field exactly where the
 *  read view drew the heading, or the swap to editing would add or drop a title line. */
function hasOwnTitle(doc: BirtvyDoc): boolean {
  const title = doc.title?.trim();
  if (!title) return false;
  if (/^\s*(#{1,6}\s|<h[1-3][\s>])/.test(doc.content ?? '')) return false;
  return title !== deriveDocTitle(doc.content);
}

export interface DocumentEditorProps {
  doc: BirtvyDoc;
  /** Context only (the read-only warning names the deck). The save path deliberately does NOT read
   *  it — the bus's registered save already closed over the right conversation, and a second copy
   *  here could only ever disagree with it. */
  conversationId?: string;
  /** True while keystrokes are buffered below the debounce; false at each handoff to the bus. */
  onDirty?: (dirty: boolean) => void;
}

export function DocumentEditor({ doc, conversationId, onDirty }: DocumentEditorProps) {
  const bus = useSyncExternalStore(subscribeDocEditBus, readDocEditBus, serverDocEditBus);
  const canEdit = !!bus && !doc.streaming && doc.resourceKey !== 'outline';

  /** Latest-value mirrors, assigned in render so the fixed-identity callbacks below never close
   *  over a stale frame — the canvas's `purgeDeletedDocsRef` pattern. */
  const busRef = useRef(bus);
  busRef.current = bus;
  const onDirtyRef = useRef(onDirty);
  onDirtyRef.current = onDirty;
  const docRef = useRef(doc);
  docRef.current = doc;

  const [titleField, setTitleField] = useState(() => doc.title ?? '');
  const [showTitle, setShowTitle] = useState(() => hasOwnTitle(doc));
  /** Written EAGERLY by the handlers (state lands a frame later, the save must not). */
  const titleRef = useRef(titleField);
  const showTitleRef = useRef(showTitle);
  showTitleRef.current = showTitle;
  const titleFocusedRef = useRef(false);
  const contentRef = useRef(doc.content);

  /** The buffered edit. `docId` is captured at QUEUE time, so a flush that runs after the mount has
   *  already been handed the next card still saves against the card that was typed into. */
  const pendingRef = useRef<{ docId: number; patch: DocEditPatch } | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flush = useCallback(() => {
    if (timerRef.current != null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    const pending = pendingRef.current;
    if (!pending) return;
    pendingRef.current = null;
    onDirtyRef.current?.(false);
    const activeBus = busRef.current;
    if (!activeBus) {
      // Unreachable while the contract holds — no bus means nothing was editable, so nothing could
      // be pending. Guarded anyway: an edit silently vanishing is the failure mode that must not
      // exist, so if it ever can, it at least says so.
      console.warn(
        `[deck15] docEditBus unregistered with an edit pending on doc ${pending.docId} — the edit was dropped`,
      );
      return;
    }
    activeBus.save(pending.docId, pending.patch);
  }, []);

  const queueSave = useCallback(() => {
    const wasClean = pendingRef.current === null;
    pendingRef.current = {
      docId: docRef.current.id,
      patch: {
        content: contentRef.current,
        // The title rule's editor half: the field's text when the doc has a name of its own, the
        // canvas's derivation when the first line IS the name. `updateDocIn` re-guards the blank.
        title: showTitleRef.current ? titleRef.current : deriveDocTitle(contentRef.current),
      },
    };
    if (wasClean) onDirtyRef.current?.(true);
    if (timerRef.current != null) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(flush, SAVE_DEBOUNCE_MS);
  }, [flush]);

  const handleContent = useCallback(
    (markdown: string) => {
      if (!busRef.current || docRef.current.streaming || docRef.current.resourceKey === 'outline') return;
      contentRef.current = markdown;
      queueSave();
    },
    [queueSave],
  );

  /**
   * The handler is ALWAYS passed and only `editable` gates it. The bus registers in the WORKSPACE's
   * effect, which runs after this component's (child effects first), so the editor is created one
   * beat before saving is possible — and `editable` is the option `useDocumentEditor` re-applies
   * live, while the update wiring is fixed at creation. `onUpdate` only fires on real user edits,
   * which cannot happen while non-editable, so the early wiring costs nothing.
   */
  const editor = useDocumentEditor(doc.content, handleContent, canEdit);

  /**
   * A different card arrived in the same mount (the host should key on `doc.id`; this holds the
   * line when it does not): flush the outgoing card's buffer, then re-seed title state from the
   * incoming one. The body re-seeds itself — `useDocumentEditor`'s own content re-sync.
   */
  useEffect(() => {
    const current = docRef.current;
    contentRef.current = current.content;
    titleRef.current = current.title ?? '';
    setTitleField(current.title ?? '');
    const own = hasOwnTitle(current);
    showTitleRef.current = own;
    setShowTitle(own);
    return () => flush();
  }, [doc.id, flush]);

  /** Adopt an outside rename (the preview bar, a directory row) — but never while the field is
   *  focused or an edit is buffered, the same "not while the user is typing" rule the body's
   *  re-sync applies. */
  useEffect(() => {
    if (titleFocusedRef.current || pendingRef.current) return;
    titleRef.current = doc.title ?? '';
    setTitleField(doc.title ?? '');
  }, [doc.title]);

  useEffect(() => {
    if (bus) return;
    console.warn(
      `[deck15] DocumentEditor on doc ${doc.id}${conversationId ? ` (deck ${conversationId})` : ''} has no docEditBus registered — rendering read-only`,
    );
  }, [bus, doc.id, conversationId]);

  return (
    <div className={cn(proseStyles.docProse, bodyStyles.body, styles.editor, guard.content)}>
      {showTitle ? (
        <input
          className={cn(proseStyles.docCardTitle, styles.titleField)}
          value={titleField}
          placeholder={DOC_TITLE_PLACEHOLDER}
          readOnly={!canEdit}
          aria-label="Document title"
          onFocus={() => {
            titleFocusedRef.current = true;
          }}
          onBlur={() => {
            titleFocusedRef.current = false;
          }}
          onChange={(event) => {
            if (!canEdit) return;
            const next = event.target.value;
            titleRef.current = next;
            setTitleField(next);
            queueSave();
          }}
          onKeyDown={(event) => {
            // A title is one line; Enter walks into the body, like every doc editor the user knows.
            if (event.key === 'Enter') {
              event.preventDefault();
              editor?.commands.focus('start');
            }
          }}
        />
      ) : null}
      {/* The canvas editor's selection toolbar, only once edits can actually land. */}
      {editor && canEdit ? <FormattingToolbar editor={editor} buttons={FORMATTING_BUTTONS} /> : null}
      <EditorContent editor={editor} />
    </div>
  );
}
